import { getLocale, t, type TutorialPhase } from '@viral-space/shared';
import {
  COLORS, FONT, GLOW, el,
  injectSharedKeyframes, isMobileLayout,
} from './ui-shared';
import type { InputState } from '../input/input-manager';
import { setInputSettings } from '../input/settings';

/**
 * `localStorage` flag remembering that the pilot already picked their
 * Y-axis preference during the tutorial intro. Once set we never ask
 * again — they can still flip it from `Pause Menu → Controls`. The key
 * is versioned so a future flow change (extra prompts, separate touch
 * vs mouse choices, etc.) can be rolled out without re-prompting every
 * existing pilot.
 */
const AIM_SETUP_STORAGE_KEY = 'vs_tutorial_aim_setup_v1';

/**
 * Scripted onboarding overlay. Rendered as a non-intrusive panel at the
 * top of the HUD (so it never covers the ship or reticle) and driven by
 * server-authoritative
 * {@link TutorialPhase} messages plus local observations (ship input,
 * resource pickups, kill confirmations). Every state transition the
 * player earns is reported back to the server via the `onAdvance`
 * callback; the server validates the transition and echoes back a
 * `TutorialPhase` message, which is the only thing that actually
 * changes which instructions the overlay renders.
 *
 * The overlay never blocks gameplay (no pointer-events on the body),
 * but it does swallow its own buttons for click handling. The
 * tutorial does not end on its own — the server teleports the pilot to
 * sector 0,0,0 when the final `done` phase is acknowledged, and the
 * overlay hides itself in response to that message.
 */
export class Tutorial {
  private container: HTMLElement;
  private root: HTMLElement;
  private panelEl!: HTMLElement;
  private titleEl!: HTMLElement;
  private bodyEl!: HTMLElement;
  private progressRowEl!: HTMLElement;
  private actionEl!: HTMLElement;
  /**
   * Always-visible footer line at the bottom of the tutorial panel
   * that points pilots at the pause-menu invert-Y toggle, so the
   * one-time aim-style chooser doesn't feel like a permanent
   * commitment. Hidden during the chooser itself (where the pilot is
   * literally being asked to pick) and rendered for every other
   * tutorial phase.
   */
  private invertHintEl!: HTMLElement;
  private flashEl!: HTMLElement;
  private completeEl: HTMLElement | null = null;

  /** Currently rendered phase, set by {@link setPhase}. */
  private phase: TutorialPhase | null = null;

  /** Movement-phase progress: which cardinal directions have been tried. */
  private moves = { fwd: false, back: false, up: false, down: false };

  /** Mining-phase progress: whether the pilot has destroyed an asteroid. */
  private shotAsteroid = false;
  /** Mining-phase progress: whether the pilot has picked up a resource. */
  private collectedDrop = false;

  /** Combat-phase progress: whether the hostile NPC has been destroyed. */
  private killedHostile = false;

  /** Stable user→server transition callback. */
  private onAdvance: (phase: TutorialPhase) => void;

  /** Optional callback fired the first time the overlay actually appears. */
  private onStart: (() => void) | null;

  /**
   * Debounce so we don't spam the server every frame while the pilot
   * holds a thrust key — we only care about the first moment a given
   * direction was seen this phase.
   */
  private advancedTo: TutorialPhase | null = null;

  /**
   * Tracks whether the one-time "choose your aim" intro overlay has
   * been resolved this session. Mirrors {@link AIM_SETUP_STORAGE_KEY}
   * so we don't re-read storage every frame.
   */
  private aimSetupResolved = false;

  constructor(
    container: HTMLElement,
    onAdvance: (phase: TutorialPhase) => void,
    onStart: (() => void) | null = null,
  ) {
    this.container = container;
    this.onAdvance = onAdvance;
    this.onStart = onStart;
    injectSharedKeyframes();

    this.root = el('div', {
      position: 'absolute',
      inset: '0',
      zIndex: '250',
      display: 'none',
      pointerEvents: 'none',
    });
    this.container.appendChild(this.root);

    this.buildOverlay();
  }

  private buildOverlay(): void {
    this.panelEl = el('div', {
      position: 'absolute',
      top: 'clamp(24px, 6vh, 72px)',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'rgba(0,10,0,0.55)',
      backdropFilter: 'blur(3px)',
      border: `1px solid ${COLORS.dim}`,
      padding: 'clamp(14px, 2.5vw, 24px) clamp(20px, 4vw, 36px)',
      maxWidth: '560px',
      width: '90vw',
      fontFamily: FONT,
      textTransform: 'uppercase',
      letterSpacing: '1px',
      boxShadow: `0 0 30px rgba(0,255,65,0.12), inset 0 0 20px rgba(0,255,65,0.03)`,
      pointerEvents: 'auto',
      textAlign: 'center',
    });
    this.root.appendChild(this.panelEl);

    this.titleEl = el('div', {
      fontSize: 'clamp(14px, 2.2vw, 20px)',
      color: COLORS.primary,
      textShadow: GLOW,
      marginBottom: '8px',
      letterSpacing: '4px',
    });
    this.panelEl.appendChild(this.titleEl);

    this.bodyEl = el('div', {
      fontSize: 'clamp(11px, 1.4vw, 14px)',
      color: COLORS.primary,
      opacity: '0.85',
      lineHeight: '1.7',
      textTransform: 'none',
      letterSpacing: '0.5px',
    });
    this.panelEl.appendChild(this.bodyEl);

    this.progressRowEl = el('div', {
      display: 'flex',
      gap: '8px',
      marginTop: '12px',
      flexWrap: 'wrap',
      justifyContent: 'center',
    });
    this.panelEl.appendChild(this.progressRowEl);

    this.actionEl = el('div', {
      marginTop: '10px',
      fontSize: '10px',
      color: COLORS.dim,
      opacity: '0.6',
      letterSpacing: '1px',
    });
    this.panelEl.appendChild(this.actionEl);

    this.invertHintEl = el('div', {
      marginTop: '10px',
      paddingTop: '8px',
      borderTop: `1px dashed ${COLORS.dim}`,
      fontSize: '10px',
      color: COLORS.primary,
      opacity: '0.55',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    });
    this.panelEl.appendChild(this.invertHintEl);

    // Full-screen green flash used as positive feedback when the pilot
    // clears a sub-step (e.g. first forward thrust, first asteroid
    // broken). Stays behind the HUD so the world is still visible.
    this.flashEl = el('div', {
      position: 'absolute',
      inset: '0',
      background: 'radial-gradient(circle at center, rgba(0,255,65,0.25), rgba(0,255,65,0) 70%)',
      opacity: '0',
      pointerEvents: 'none',
      transition: 'opacity 0.45s ease-out',
    });
    this.root.appendChild(this.flashEl);
  }

  /**
   * Update which phase of the onboarding flow is currently displayed.
   * Called by the networking layer whenever a `TutorialPhase` message
   * arrives. Passing `'done'` dismisses the overlay and shows the
   * completion panel with the graduation button.
   */
  setPhase(phase: TutorialPhase): void {
    const firstAppearance = this.phase === null && this.root.style.display === 'none';
    this.phase = phase;
    this.advancedTo = null;

    if (phase === 'movement') {
      this.moves = { fwd: false, back: false, up: false, down: false };
    } else if (phase === 'mining') {
      this.shotAsteroid = false;
      this.collectedDrop = false;
    } else if (phase === 'combat') {
      this.killedHostile = false;
    }

    if (phase === 'done') {
      // The combat step's own completion handler already showed the
      // graduation panel, so here we just tear down the ongoing
      // overlay — the server will teleport the player to 0,0,0.
      this.panelEl.style.display = 'none';
      return;
    }

    this.panelEl.style.display = '';
    this.root.style.display = 'block';
    if (firstAppearance) this.onStart?.();

    // Pilots who haven't picked an aim style yet see the inverted-axis
    // chooser before any movement chips. The choice persists to
    // localStorage and is also reflected in the live input settings, so
    // the very first thrust they try already feels right. We don't gate
    // the mining / combat phases on this — if the pilot crashed the tab
    // mid-tutorial they still flow through the rest of the steps even
    // though they got asked once already.
    if (phase === 'movement' && !this.hasResolvedAimSetup()) {
      this.renderAimSetup();
      return;
    }

    this.render();
    this.playFlash();
  }

  /**
   * Returns true while the overlay is visible on screen. Used by
   * client input-gating so Escape / map / other UI don't fight the
   * tutorial for focus.
   */
  isActive(): boolean {
    return this.root.style.display !== 'none';
  }

  /**
   * Observe ship input. During the movement phase, the first time each
   * cardinal direction is pressed the matching chip flips on; once all
   * four are ticked we request the next phase.
   */
  observeInput(input: InputState): void {
    if (this.phase !== 'movement') return;
    if (this.advancedTo) return;
    const THRESH = 0.2;
    let changed = false;
    if (!this.moves.fwd && input.thrust.z > THRESH) { this.moves.fwd = true; changed = true; }
    if (!this.moves.back && input.thrust.z < -THRESH) { this.moves.back = true; changed = true; }
    if (!this.moves.up && input.thrust.y > THRESH) { this.moves.up = true; changed = true; }
    if (!this.moves.down && input.thrust.y < -THRESH) { this.moves.down = true; changed = true; }
    if (changed) {
      this.render();
      this.playFlash();
    }
    if (this.moves.fwd && this.moves.back && this.moves.up && this.moves.down) {
      this.requestAdvance('mining');
    }
  }

  /** Called whenever a server HitConfirm for a destroyed asteroid arrives. */
  notifyAsteroidDestroyed(): void {
    if (this.phase !== 'mining') return;
    if (!this.shotAsteroid) {
      this.shotAsteroid = true;
      this.playFlash();
      this.render();
    }
  }

  /**
   * Called whenever the server reports a resource pickup for the local
   * pilot. If we're in the mining phase, this satisfies the collection
   * sub-step and advances us to combat.
   */
  notifyResourceCollected(): void {
    if (this.phase !== 'mining') return;
    if (!this.collectedDrop) {
      this.collectedDrop = true;
      this.playFlash();
      this.render();
      this.requestAdvance('combat');
    }
  }

  /**
   * Called whenever a server HitConfirm for a destroyed hostile NPC
   * arrives. In the combat phase this completes the step and pops the
   * graduation panel; the pilot clicks its button to request `done`.
   */
  notifyNpcKilled(): void {
    if (this.phase !== 'combat') return;
    if (this.killedHostile) return;
    this.killedHostile = true;
    this.playFlash();
    this.showCompletionPanel();
  }

  private requestAdvance(next: TutorialPhase): void {
    if (this.advancedTo === next) return;
    this.advancedTo = next;
    this.onAdvance(next);
  }

  /**
   * Storage-backed gate for the one-time aim-axis chooser. We re-read
   * it lazily so a pilot who pressed "INVERTED" earlier in the same
   * session never gets re-prompted (e.g. if a `TutorialPhase` echo
   * arrives twice for whatever reason).
   */
  private hasResolvedAimSetup(): boolean {
    if (this.aimSetupResolved) return true;
    try {
      if (localStorage.getItem(AIM_SETUP_STORAGE_KEY) === 'true') {
        this.aimSetupResolved = true;
        return true;
      }
    } catch {
      // localStorage may be blocked (private mode / strict cookie
      // policy); treat as "not resolved" so the prompt still shows
      // — worst case the pilot picks again on the next session.
    }
    return false;
  }

  private markAimSetupResolved(): void {
    this.aimSetupResolved = true;
    try {
      localStorage.setItem(AIM_SETUP_STORAGE_KEY, 'true');
    } catch {
      /* see hasResolvedAimSetup */
    }
  }

  /**
   * Replace the standard movement-phase content with a "choose your
   * aim style" chooser. Resolves to either `invertYMouse: true` or
   * `invertYTouch: true` based on the current device, persists the
   * choice via {@link setInputSettings}, then drops back into the
   * regular movement-phase render.
   */
  private renderAimSetup(): void {
    const mobile = isMobileLayout();
    this.progressRowEl.innerHTML = '';

    // The chooser is the very first thing a new pilot sees and the
    // buttons need to be clickable. Exit pointer lock so the cursor is
    // visible — the input manager will reacquire lock on the next
    // canvas click after they pick. This matches the same defensive
    // call we make in `showCompletionPanel`.
    if (typeof document !== 'undefined' && document.pointerLockElement) {
      document.exitPointerLock();
    }

    this.titleEl.textContent = t('tutorial.aimSetup.title');
    this.bodyEl.textContent = mobile
      ? t('tutorial.aimSetup.bodyTouch')
      : t('tutorial.aimSetup.bodyMouse');
    this.actionEl.textContent = t('tutorial.aimSetup.changeHint');
    this.actionEl.style.opacity = '0.85';
    // The chooser itself already explains they can change it later via
    // the body + actionEl, so the bottom hint would be redundant noise
    // here. Hide it for this one screen and restore it on the next
    // render (movement / mining / combat / etc.).
    this.invertHintEl.textContent = '';
    this.invertHintEl.style.display = 'none';

    const buttons = el('div', {
      display: 'flex',
      gap: '12px',
      justifyContent: 'center',
      flexWrap: 'wrap',
      marginTop: '4px',
    });

    // The button visuals are weighted toward NORMAL: solid 2px border,
    // a "RECOMMENDED" badge above the title, and a brighter idle
    // background. INVERTED is rendered as a quiet ghost button so a
    // pilot who isn't sure which to pick gravitates to the safe
    // default that matches Star Citizen / No Man's Sky / Elite. Both
    // remain fully clickable — we never auto-confirm.
    const makeButton = (
      label: string,
      desc: string,
      onPick: () => void,
      opts: { highlighted: boolean; badge?: string },
    ): HTMLElement => {
      const { highlighted, badge } = opts;
      const idleBg = highlighted ? 'rgba(0,255,65,0.12)' : 'transparent';
      const hoverBg = highlighted ? 'rgba(0,255,65,0.22)' : 'rgba(0,255,65,0.12)';
      const btn = el('button', {
        fontFamily: FONT,
        background: idleBg,
        border: highlighted
          ? `2px solid ${COLORS.primary}`
          : `1px solid ${COLORS.dim}`,
        color: COLORS.primary,
        padding: highlighted ? '11px 17px' : '12px 18px',
        cursor: 'pointer',
        textTransform: 'uppercase',
        letterSpacing: '2px',
        fontSize: '12px',
        textShadow: highlighted ? GLOW : 'none',
        textAlign: 'center',
        minWidth: '180px',
        maxWidth: '240px',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        opacity: highlighted ? '1' : '0.8',
        boxShadow: highlighted ? `0 0 18px rgba(0,255,65,0.18)` : 'none',
        pointerEvents: 'auto',
      });
      if (badge) {
        const badgeEl = el('span', {
          fontSize: '9px',
          letterSpacing: '3px',
          color: '#001008',
          background: COLORS.primary,
          padding: '2px 8px',
          alignSelf: 'center',
          textShadow: 'none',
        }, badge);
        btn.appendChild(badgeEl);
      }
      const titleSpan = el('span', {
        fontSize: '13px',
        letterSpacing: '4px',
      }, label);
      const descSpan = el('span', {
        fontSize: '11px',
        opacity: '0.75',
        letterSpacing: '0.5px',
        textTransform: 'none',
      }, desc);
      btn.appendChild(titleSpan);
      btn.appendChild(descSpan);
      btn.addEventListener('mouseenter', () => {
        btn.style.background = hoverBg;
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.background = idleBg;
      });
      btn.addEventListener('click', () => onPick());
      return btn;
    };

    const apply = (invert: boolean): void => {
      // Set the live input setting on whichever surface the pilot is
      // most likely to use first. The other flag is left at its
      // current value so a Surface / iPad-with-mouse pilot's earlier
      // choice survives the on-the-fly device swap.
      if (mobile) {
        setInputSettings({ invertYTouch: invert });
      } else {
        setInputSettings({ invertYMouse: invert });
      }
      this.markAimSetupResolved();
      this.render();
      this.playFlash();
    };

    buttons.appendChild(makeButton(
      t('tutorial.aimSetup.normal'),
      mobile
        ? t('tutorial.aimSetup.normalDescTouch')
        : t('tutorial.aimSetup.normalDescMouse'),
      () => apply(false),
      { highlighted: true, badge: t('tutorial.aimSetup.recommended') },
    ));
    buttons.appendChild(makeButton(
      t('tutorial.aimSetup.invert'),
      mobile
        ? t('tutorial.aimSetup.invertDescTouch')
        : t('tutorial.aimSetup.invertDescMouse'),
      () => apply(true),
      { highlighted: false },
    ));
    this.progressRowEl.appendChild(buttons);
  }

  private render(): void {
    if (!this.phase) return;
    const es = getLocale() === 'es';

    const text = (en: string, esText: string) => (es ? esText : en);

    this.progressRowEl.innerHTML = '';
    // Persistent reminder that the inverted-Y choice isn't sticky.
    // Restored every render in case the chooser hid it earlier.
    this.invertHintEl.textContent = `// ${t('tutorial.aimSetup.changeHintShort')} //`;
    this.invertHintEl.style.display = '';
    // Reset actionEl opacity in case the chooser raised it — phase
    // copy is meant to feel quieter than the body.
    this.actionEl.style.opacity = '0.6';

    switch (this.phase) {
      case 'movement': {
        this.titleEl.textContent = `// ${text('FLIGHT TRAINING', 'ENTRENAMIENTO DE VUELO')} //`;
        this.bodyEl.textContent = text(
          'Try every thrust direction to prove you can fly. Forward, backward, up, and down.',
          'Prueba todas las direcciones: avanzar, retroceder, subir y bajar.',
        );
        this.addChip(
          text('W · FORWARD', 'W · AVANZAR'),
          this.moves.fwd,
        );
        this.addChip(
          text('S · BACKWARD', 'S · RETROCEDER'),
          this.moves.back,
        );
        this.addChip(
          text('SPACE · UP', 'ESPACIO · SUBIR'),
          this.moves.up,
        );
        this.addChip(
          text('C · DOWN', 'C · BAJAR'),
          this.moves.down,
        );
        this.actionEl.textContent = text(
          'Hold each key briefly. The room will reset when you finish.',
          'Mantén cada tecla un momento. La sala avanzará al terminar.',
        );
        break;
      }
      case 'mining': {
        this.titleEl.textContent = `// ${text('MINING TRAINING', 'ENTRENAMIENTO DE MINERÍA')} //`;
        this.bodyEl.textContent = text(
          'Shoot the asteroids with LEFT CLICK, then fly through the glowing drops to collect the resources.',
          'Dispara a los asteroides con CLIC IZQUIERDO y vuela sobre los restos para recolectar.',
        );
        this.addChip(
          text('BREAK AN ASTEROID', 'ROMPE UN ASTEROIDE'),
          this.shotAsteroid,
        );
        this.addChip(
          text('COLLECT A DROP', 'RECOGE UN RESTO'),
          this.collectedDrop,
        );
        this.actionEl.textContent = text(
          'Asteroids have no armour here. One drop is enough.',
          'Los asteroides son blandos aquí. Un recurso es suficiente.',
        );
        break;
      }
      case 'combat': {
        this.titleEl.textContent = `// ${text('COMBAT TRAINING', 'ENTRENAMIENTO DE COMBATE')} //`;
        this.bodyEl.textContent = text(
          'A hostile scout is in the room with you. It hits for 1 point of damage. Destroy it to graduate.',
          'Hay un explorador hostil en la sala. Sus disparos hacen 1 de daño. Destrúyelo para graduarte.',
        );
        this.addChip(
          text('ELIMINATE THE TARGET', 'ELIMINA AL OBJETIVO'),
          this.killedHostile,
        );
        this.actionEl.textContent = text(
          'Lead your shots. Your starter weapon has infinite ammo.',
          'Anticipa tus disparos. Tu arma inicial tiene munición infinita.',
        );
        break;
      }
      case 'done': {
        // Handled by setPhase; nothing to render here.
        break;
      }
    }
  }

  private addChip(label: string, done: boolean): void {
    const chip = el('span', {
      padding: '4px 10px',
      border: `1px solid ${done ? COLORS.primary : COLORS.dim}`,
      color: done ? '#001008' : COLORS.primary,
      background: done ? COLORS.primary : 'transparent',
      fontSize: '11px',
      letterSpacing: '1px',
      textShadow: done ? 'none' : `0 0 4px rgba(0,255,65,0.4)`,
      transition: 'all 0.2s',
    }, (done ? '✓  ' : '·  ') + label);
    this.progressRowEl.appendChild(chip);
  }

  private playFlash(): void {
    this.flashEl.style.opacity = '1';
    setTimeout(() => { this.flashEl.style.opacity = '0'; }, 250);
  }

  /**
   * Render the end-of-tutorial congratulations panel with the
   * "Go to Space" button. Clicking the button fires a
   * {@link TutorialAdvance}('done') request; the server validates it
   * (combat step must be complete) and teleports the pilot to 0,0,0.
   * Falls back to a safe re-render if the server rejects the request.
   */
  private showCompletionPanel(): void {
    this.panelEl.style.display = 'none';

    // Release pointer lock so the pilot can actually see and click the
    // "GO TO SPACE" button. The main input manager will re-acquire lock
    // on the next canvas click after graduation.
    if (typeof document !== 'undefined' && document.pointerLockElement) {
      document.exitPointerLock();
    }

    if (this.completeEl) this.completeEl.remove();
    const es = getLocale() === 'es';
    const wrap = el('div', {
      position: 'absolute',
      inset: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,8,0,0.6)',
      pointerEvents: 'auto',
      cursor: 'default',
    });
    const card = el('div', {
      background: 'rgba(0,12,0,0.96)',
      border: `1px solid ${COLORS.primary}`,
      padding: '32px 44px',
      maxWidth: '520px',
      width: '92vw',
      fontFamily: FONT,
      textAlign: 'center',
      boxShadow: `0 0 40px rgba(0,255,65,0.25), inset 0 0 30px rgba(0,255,65,0.06)`,
    });
    wrap.appendChild(card);

    const title = el('div', {
      fontSize: 'clamp(14px, 2.4vw, 24px)',
      letterSpacing: '3px',
      color: COLORS.primary,
      textShadow: GLOW,
      marginBottom: '14px',
      whiteSpace: 'nowrap',
    }, es ? '// ¡ENTRENAMIENTO COMPLETADO! //' : '// TRAINING COMPLETE //');
    card.appendChild(title);

    const body = el('div', {
      fontSize: '13px',
      color: COLORS.primary,
      opacity: '0.85',
      textTransform: 'none',
      lineHeight: '1.7',
      marginBottom: '22px',
    }, es
      ? 'Has dominado los fundamentos: vuelo, minería y combate. El universo te espera, piloto.'
      : 'You have mastered the fundamentals: flight, mining, and combat. The universe awaits, pilot.');
    card.appendChild(body);

    const btn = el('button', {
      fontFamily: FONT,
      fontSize: '14px',
      padding: '14px 40px',
      background: 'transparent',
      border: `2px solid ${COLORS.primary}`,
      color: COLORS.primary,
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: '6px',
      transition: 'all 0.2s',
      pointerEvents: 'auto',
    }, es ? 'IR AL ESPACIO' : 'GO TO SPACE');
    btn.addEventListener('mouseenter', () => {
      btn.style.background = COLORS.primary;
      btn.style.color = '#001008';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = 'transparent';
      btn.style.color = COLORS.primary;
    });
    btn.addEventListener('click', () => {
      btn.setAttribute('disabled', 'true');
      btn.textContent = es ? 'VIAJANDO…' : 'JUMPING…';
      this.requestAdvance('done');
    });
    card.appendChild(btn);

    this.root.appendChild(wrap);
    this.completeEl = wrap;
  }

  /**
   * Hide and dispose of all overlay DOM. Safe to call when the pilot
   * graduates to sector 0,0,0 or at game shutdown.
   */
  hide(): void {
    this.root.style.display = 'none';
    if (this.completeEl) {
      this.completeEl.remove();
      this.completeEl = null;
    }
  }

  dispose(): void {
    this.root.remove();
  }

  // Unused in the server-authoritative flow, but kept so callers from
  // the previous (client-only) tutorial don't need to be refactored in
  // two steps. No-op until `setPhase` is called by the server.
  start(): void { /* no-op */ }
}

/** Re-export the shared phase type for convenience on the client side. */
export type { TutorialPhase };
